/**
 * ===========================================================================
 * OPERATING JURISDICTIONS — THE PURE DECISIONS
 * ===========================================================================
 *
 * Everything on this screen that could be wrong lives here rather than inside
 * a JSX ternary, for the reason `docs/intelligence/audit/
 * UNKNOWN-IS-NOT-DENIED-FRONTEND.md` gives at `Visits.tsx:206`: branches that
 * cannot all be reached from a mount "read as covered while nothing exercises
 * them". A closed effective window, a resolution that fell through to the
 * organization default, and a historical answer that is not actually
 * reproducible are all states a mount can only reach with the right fixture —
 * so each one is a function with a case per branch.
 *
 * NONE OF THIS IS A SECOND SOURCE OF TRUTH. The server decides which
 * jurisdictions apply, at which scope, from which basis; these functions decide
 * only how to SAY it.
 */
import type { organization } from '@cwos/api-client';

type Jurisdictions = typeof organization.jurisdictions;
export type OperatingJurisdiction = Awaited<ReturnType<Jurisdictions['list']>>[number];
export type JurisdictionResolution = Awaited<ReturnType<Jurisdictions['resolve']>>['jurisdiction'];
export type HistoricalResolution = Awaited<ReturnType<Jurisdictions['resolve']>>['asKnownAt'];

/**
 * WHERE A ROW SITS RELATIVE TO TODAY, and the three are kept apart.
 *
 * `ended` is the one that matters and the reason this is not a boolean: a
 * jurisdiction with a closed window MUST NOT RENDER AS CURRENT. It is the
 * answer to "were we licensed there when that visit happened", which is a
 * different sentence from "we operate there", and a screen that showed the
 * second when the truth is the first would be telling an auditor something
 * false about today.
 *
 * `scheduled` is the mirror image — recorded ahead of time, not yet in force —
 * and collapsing it into `current` would claim a jurisdiction the organization
 * does not yet operate under.
 */
export type WindowState = 'current' | 'ended' | 'scheduled';

/**
 * The upper bound is INCLUSIVE, matching `selectOperatingJurisdictions` and
 * `intelligence/library/applicability.ts`. The half-open `[)` convention
 * belongs to the exclusion constraint, which answers a different question
 * (overlap), and 1830000000000 puts the warning on the column itself because
 * two conventions in one table is exactly what gets "corrected" into a bug.
 *
 * `today` is a parameter and never `new Date()` inside: a fixture and this
 * function must compute the same window in the same timezone, and a screen
 * whose classification depends on the reader's clock is a screen that changes
 * its mind at midnight.
 */
export function windowStateOf(
  row: Pick<OperatingJurisdiction, 'effectiveFrom' | 'effectiveTo'>,
  today: string,
): WindowState {
  if (row.effectiveFrom > today) return 'scheduled';
  if (row.effectiveTo !== null && row.effectiveTo < today) return 'ended';
  return 'current';
}

/** Copy and severity per window state. Text always, never colour alone. */
export function windowLabel(state: WindowState): { label: string; variant: 'good' | 'plain' } {
  switch (state) {
    case 'current':
      return { label: 'In force', variant: 'good' };
    case 'ended':
      return { label: 'Ended', variant: 'plain' };
    case 'scheduled':
      return { label: 'Not yet in force', variant: 'plain' };
  }
}

/**
 * HOW THE SCOPE READS, and the org row is deliberately not called a fact about
 * a program.
 *
 * An organization-wide row is a DEFAULT — it applies where nothing narrower
 * does, and it never outranks a program, site or service row. Labelling it
 * "Whole organization" alone would let a reader take it for the answer for
 * every program, which is the precedence error the resolver exists to prevent,
 * committed on screen instead of in code.
 *
 * A scope whose name did not come back is NAMED AS MISSING rather than widened:
 * a program-scoped jurisdiction whose program cannot be named is still
 * program-scoped, and rendering it as organization-wide would broaden it.
 */
export function scopeLabel(scope: OperatingJurisdiction['scope']): string {
  switch (scope.kind) {
    case 'org':
      return 'Organization default';
    case 'program':
      return scope.name === null ? 'A program (name unavailable)' : `Program · ${scope.name}`;
    case 'location':
      return scope.name === null ? 'A site (name unavailable)' : `Site · ${scope.name}`;
    case 'service':
      return scope.name === null ? 'A service (name unavailable)' : `Service · ${scope.name}`;
  }
}

export function revisionActLabel(act: string): string {
  switch (act) {
    case 'recorded':
      return 'Recorded';
    case 'ended':
      return 'Ended';
    default:
      return act;
  }
}

export function revisionScopeLabel(scopeType: string): string {
  switch (scopeType) {
    case 'org':
      return 'Organization default';
    case 'program':
      return 'A program';
    case 'location':
      return 'A site';
    case 'service':
      return 'A service';
    default:
      return scopeType;
  }
}

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

export function endRecordedLate(
  revision: organization.jurisdictions.JurisdictionRevision,
): boolean {
  if (revision.act !== 'ended' || revision.effectiveTo === null) return false;
  const recordedAt = new Date(revision.recordedAt);
  const effectiveTo = new Date(`${revision.effectiveTo}T00:00:00`);
  if (Number.isNaN(recordedAt.getTime()) || Number.isNaN(effectiveTo.getTime())) return false;
  return localDayKey(recordedAt) > localDayKey(effectiveTo);
}

/** True for the rows that are a default rather than a recorded narrowing. */
export function isDefaultScope(scope: OperatingJurisdiction['scope']): boolean {
  return scope.kind === 'org';
}

/**
 * THE FOUR STATES OF A RESOLUTION, AS FOUR SENTENCES.
 *
 * They must not collapse, and three of the four carry no jurisdictions at all:
 *
 *   resolved                  here is the answer, and where it came from.
 *   context_absent            more than one scope could apply and the question
 *                             did not narrow it, so the WIDE set is shown —
 *                             more floors, never fewer.
 *   no_jurisdiction_recorded  NOT "no requirement applies". Nobody wrote down
 *                             where this organization operates, which is a
 *                             thing to fix rather than a clean bill of health.
 *   context_refused           the reader may not ask about the program, site or
 *                             service they named. An answer about THEM, not
 *                             about the organization.
 */
export interface ResolutionSummary {
  readonly kind: 'resolved' | 'wide' | 'unrecorded' | 'refused';
  readonly headline: string;
  readonly detail: string;
  readonly jurisdictions: readonly string[];
  /** True only when the answer came from `organization.jurisdiction`. */
  readonly fromDefault: boolean;
}

export function summarizeResolution(resolution: JurisdictionResolution): ResolutionSummary {
  if (resolution === null) {
    return {
      kind: 'unrecorded',
      headline: 'No answer was asked for',
      detail: 'Choose a context and ask which jurisdictions govern it.',
      jurisdictions: [],
      fromDefault: false,
    };
  }
  switch (resolution.state) {
    case 'resolved':
      return {
        kind: 'resolved',
        headline: `Governed by ${resolution.jurisdictions.join(', ')}`,
        detail:
          resolution.basis === 'organization_default'
            ? 'From the organization’s default setting — nothing more specific is recorded for ' +
              'this context. Record an operating jurisdiction to make this a fact rather than a ' +
              'fallback.'
            : `From a recorded operating jurisdiction at the ${resolution.via} level.`,
        jurisdictions: resolution.jurisdictions,
        fromDefault: resolution.basis === 'organization_default',
      };
    case 'context_absent':
      return {
        kind: 'wide',
        headline: `Could be ${resolution.jurisdictions.join(', ')}`,
        detail:
          'This organization records jurisdictions at a level the question did not name (' +
          `${resolution.undecided.join(', ')}), so every one that could apply is shown. Name the ` +
          `${resolution.undecided[0] ?? 'context'} to narrow it.`,
        jurisdictions: resolution.jurisdictions,
        fromDefault: false,
      };
    case 'no_jurisdiction_recorded':
      return {
        kind: 'unrecorded',
        // NOT "no requirements apply". The two are different answers and only
        // one of them is good news; collapsing them is the sixth instance of
        // the four-state collapse arriving through the browser.
        headline: 'No operating jurisdiction is recorded',
        detail:
          'This is not the same as “no external requirements apply” — it means we cannot say ' +
          'which ones do. Record where this organization operates.',
        jurisdictions: [],
        fromDefault: false,
      };
    case 'context_refused':
      return {
        kind: 'refused',
        headline: `You can’t ask about that ${resolution.dimension}`,
        detail:
          `Resolving by ${resolution.dimension} needs the permission that opens ` +
          `${resolution.dimension === 'location' ? 'sites' : resolution.dimension === 'service' ? 'the service catalogue' : 'programs'}. ` +
          'This says nothing about whether a jurisdiction is recorded for it.',
        jurisdictions: [],
        fromDefault: false,
      };
  }
}

/**
 * WHETHER A HISTORICAL ANSWER IS ACTUALLY A HISTORICAL ANSWER.
 *
 * `default_not_reproducible` means the resolver fell through to
 * `organization.jurisdiction`, which has no history: the answer restates
 * today's configuration, not the past one. Saying so is the whole point —
 * presenting it with a confident past date would be a worse failure than
 * refusing to answer, because nothing on screen would look wrong.
 */
export function reproducibilityNote(asKnownAt: HistoricalResolution): string | null {
  if (asKnownAt === null) return null;
  if (asKnownAt.reproducible === 'reproducible') return null;
  return (
    'This answer came from the organization’s default setting, which keeps no history — so it ' +
    'shows what that setting says TODAY, not what it said then. Record an operating ' +
    'jurisdiction if this date needs to be answerable later.'
  );
}
